/*! Copyright (c) Safe As Milk. All rights reserved. */
import PageAnimations, { Animations } from "animations";

import { debounce } from "utils";

class TabbedContent extends HTMLElement {
    static #panelRequests=new Map;
    connectedCallback() {
        this.tabs = this.querySelectorAll("tab-triggers button");
        this.tabPanels = this.querySelectorAll("tab-panel");
        this.tabList = this.querySelector("tab-triggers");
        this.currentTab = Array.from(this.tabs).find((el => el.getAttribute("aria-selected") === "true"));
        this.currentTabIndex = Array.from(this.tabs).findIndex((el => el.getAttribute("aria-selected") === "true"));
        this.tabs.forEach((tab => {
            tab.addEventListener("click", (e => this.openTab(e.target.id)));
        }));
        this.tabList.addEventListener("keydown", (e => {
            if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                this.tabs[this.currentTabIndex].setAttribute("tabindex", -1);
                if (e.key === "ArrowRight") {
                    this.currentTabIndex += 1;
                    if (this.currentTabIndex >= this.tabs.length) {
                        this.currentTabIndex = 0;
                    }
                } else if (e.key === "ArrowLeft") {
                    this.currentTabIndex -= 1;
                    if (this.currentTabIndex < 0) {
                        this.currentTabIndex = this.tabs.length - 1;
                    }
                }
                this.tabs[this.currentTabIndex].setAttribute("tabindex", 0);
                this.tabs[this.currentTabIndex].focus();
            }
        }));
        if (this.dataset.sectionId) this.#initDeferredPanels();
    }
    #initDeferredPanels() {
        this.tabs.forEach((tab => {
            const prefetch = () => {
                const panel = this.#panelFor(tab);
                if (panel?.dataset.panelPage) this.#requestPanel(panel).catch((() => {}));
            };
            tab.addEventListener("pointerenter", prefetch, {
                once: true
            });
            tab.addEventListener("focus", prefetch, {
                once: true
            });
        }));
        this.addEventListener("click", (e => {
            const retry = e.target.closest("[data-tab-panel-retry]");
            if (retry) this.#renderPanel(retry.closest("tab-panel"));
        }));
        this.#renderPanel(this.#panelFor(this.currentTab));
    }
    #panelFor(tab) {
        return tab ? this.querySelector(`#${tab.getAttribute("aria-controls")}`) : null;
    }
    #panelUrl(page) {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set("section_id", this.dataset.sectionId);
        url.searchParams.set("page", page);
        const theme = window.Shopify?.theme;
        if (theme?.id && theme.role !== "main") url.searchParams.set("preview_theme_id", theme.id);
        return url.toString();
    }
    #requestPanel(panel) {
        const url = this.#panelUrl(panel.dataset.panelPage);
        if (!TabbedContent.#panelRequests.has(url)) {
            const request = fetch(url, {
                credentials: "same-origin"
            }).then((response => {
                if (!response.ok) throw new Error(`Tab panel request failed: ${response.status}`);
                return response.text();
            }));
            request.catch((() => TabbedContent.#panelRequests.delete(url)));
            TabbedContent.#panelRequests.set(url, request);
        }
        return TabbedContent.#panelRequests.get(url);
    }
    static #setPanelState(panel, state) {
        panel.dataset.panelState = state;
        panel.setAttribute("aria-busy", String(state === "loading"));
        const loading = panel.querySelector("[data-tab-panel-loading]");
        const error = panel.querySelector("[data-tab-panel-error]");
        if (loading) loading.hidden = state !== "loading";
        if (error) error.hidden = state !== "error";
    }
    async #renderPanel(panel) {
        if (!panel?.dataset.panelPage || panel.dataset.panelState === "loading") return;
        const url = this.#panelUrl(panel.dataset.panelPage);
        const focusWasInside = panel.contains(document.activeElement);
        TabbedContent.#setPanelState(panel, "loading");
        if (focusWasInside) panel.focus({
            preventScroll: true
        });
        try {
            const html = await this.#requestPanel(panel);
            const source = (new DOMParser).parseFromString(html, "text/html").getElementById(panel.id);
            if (!source || source.hasAttribute("data-panel-page")) throw new Error("Tab panel missing from response");
            panel.replaceChildren(...source.childNodes);
            delete panel.dataset.panelPage;
            delete panel.dataset.panelState;
            panel.setAttribute("aria-busy", "false");
            TabbedContent.#panelRequests.delete(url);
            PageAnimations.setup(panel);
            try {
                window._swat?.initializeActionButtons?.(`#${panel.id}`);
            } catch {}
        } catch (error) {
            TabbedContent.#setPanelState(panel, "error");
            if (focusWasInside) panel.querySelector("[data-tab-panel-retry]")?.focus();
        }
    }
    openTab(id) {
        if (this.currentTab.id === id) return;
        this.currentTab = Array.from(this.tabs).find((t => t.id === id));
        this.tabList.querySelectorAll('[aria-selected="true"]').forEach((t => {
            t.setAttribute("aria-selected", false);
            t.setAttribute("tabindex", "-1");
        }));
        this.currentTab.setAttribute("aria-selected", true);
        this.currentTab.setAttribute("tabindex", "0");
        Array.from(this.tabPanels).forEach((p => {
            p.setAttribute("hidden", true);
            const animationElement = p.querySelector(".js-animate-sequence");
            Animations.resetAnimations(animationElement);
        }));
        const newSelectedPanel = this.querySelector(`#${this.currentTab.getAttribute("aria-controls")}`);
        newSelectedPanel.removeAttribute("hidden");
        this.#renderPanel(newSelectedPanel);
    }
}

customElements.define("tabbed-content", TabbedContent);

class TabTriggers extends HTMLElement {
    #triggersObserver;
    constructor() {
        super();
        this.#triggersObserver = null;
        this.alignItems = this.parentElement.getAttribute("align-items") || "left";
    }
    connectedCallback() {
        this.#initTriggersObserver();
        this.#initLinkUpdate();
        const resizeObserver = new ResizeObserver(debounce((() => {
            this.#updateControls();
        }), 50));
        resizeObserver.observe(this);
    }
    #initTriggersObserver() {
        const callback = mutationList => {
            mutationList.forEach((mutation => {
                if (mutation.type === "attributes" && mutation.attributeName === "aria-selected" && mutation.target.getAttribute("aria-selected", "true")) {
                    this.#moveToSelected();
                }
            }));
        };
        this.#triggersObserver = new MutationObserver(callback);
    }
    #initLinkUpdate() {
        const triggers = this.querySelectorAll("button");
        const anchor = this.closest(".section").querySelector(".js-update-link");
        if (anchor) {
            Array.from(triggers).forEach((trigger => {
                trigger.addEventListener("click", (() => {
                    const link = trigger.dataset.updateLink;
                    if (link) {
                        anchor.setAttribute("href", link);
                    }
                }));
            }));
        }
    }
    #moveToSelected() {
        if (![ "left", "center" ].includes(this.alignItems)) return;
        const selected = this.querySelector('[aria-selected="true"]');
        const paddingLeft = Number(window.getComputedStyle(this).paddingLeft.replace("px", ""));
        if (selected) {
            const newPosition = this.alignItems === "left" ? selected.offsetLeft - paddingLeft : selected.offsetLeft - (this.offsetWidth - selected.offsetWidth) / 2;
            this.scrollTo({
                left: newPosition,
                behavior: "smooth"
            });
        }
    }
    #updateControls() {
        if (this.scrollWidth > this.offsetWidth) {
            const triggers = this.querySelectorAll("button");
            Array.from(triggers).forEach((trigger => {
                this.#triggersObserver.observe(trigger, {
                    attributes: true,
                    childList: false,
                    subtree: false
                });
            }));
            this.#moveToSelected();
        } else {
            this.#triggersObserver.disconnect();
        }
    }
}

customElements.define("tab-triggers", TabTriggers);
//# sourceMappingURL=tabbed-content.js.map
